/**
 * Email Logs Tab - View Email Communication History
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Refresh as PollIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { getEmailLogs, triggerEmailPoll, EmailLog } from '../../../services/supplierDeclarationService';

const EmailLogsTab: React.FC = () => {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  
  // Filters
  const [emailTypeFilter, setEmailTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Pagination
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);

  useEffect(() => {
    loadLogs();
  }, [emailTypeFilter, statusFilter, skip]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEmailLogs(
        undefined,
        undefined,
        emailTypeFilter || undefined,
        statusFilter || undefined,
        skip,
        limit
      );
      setLogs(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load email logs');
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandRow = (logId: string) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'failed':
      case 'bounced':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getEmailTypeLabel = (emailType: string) => {
    const labels: Record<string, string> = {
      request: 'Declaration Request',
      reminder_30: '30-Day Reminder',
      reminder_7: '7-Day Reminder',
      expired: 'Expired Notice',
      response: 'Response',
    };
    return labels[emailType] || emailType;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleClearFilters = () => {
    setEmailTypeFilter('');
    setStatusFilter('');
    setSkip(0);
  };

  const handleTriggerPoll = async () => {
    setPolling(true);
    try {
      const result = await triggerEmailPoll();
      if (result.emails_found === 0) {
        toast.info(result.message || 'No new supplier emails found in inbox');
      } else {
        toast.success(`Found ${result.emails_found} email(s), processed ${result.attachments_processed} attachment(s)`);
      }
      loadLogs();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to poll emails');
    } finally {
      setPolling(false);
    }
  };

  const handleLoadMore = () => {
    setSkip(skip + limit);
  };

  const handleLoadPrevious = () => {
    setSkip(Math.max(0, skip - limit));
  };

  if (loading && logs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Email Communication Logs</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={polling ? <CircularProgress size={16} /> : <PollIcon />}
            onClick={handleTriggerPoll}
            disabled={polling}
          >
            {polling ? 'Checking...' : 'Check Inbox Now'}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Email Type</InputLabel>
              <Select
                value={emailTypeFilter}
                onChange={(e) => {
                  setEmailTypeFilter(e.target.value);
                  setSkip(0);
                }}
                label="Email Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="request">Declaration Request</MenuItem>
                <MenuItem value="reminder_30">30-Day Reminder</MenuItem>
                <MenuItem value="reminder_7">7-Day Reminder</MenuItem>
                <MenuItem value="expired">Expired Notice</MenuItem>
                <MenuItem value="response">Response</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setSkip(0);
                }}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="bounced">Bounced</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={!emailTypeFilter && !statusFilter}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {logs.length === 0 && !loading ? (
        <Alert severity="info">No email logs found</Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50}></TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(log.id)}
                        >
                          {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{formatDate(log.sent_at)}</TableCell>
                      <TableCell>{log.supplier_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={getEmailTypeLabel(log.email_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{log.subject}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          color={getStatusColor(log.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Email Body:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                bgcolor: 'white',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.300',
                              }}
                            >
                              {log.body}
                            </Typography>
                            {log.message_id && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Message ID: {log.message_id}
                              </Typography>
                            )}
                            {log.error_message && (
                              <Alert severity="error" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                  Error: {log.error_message}
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleLoadPrevious}
              disabled={skip === 0 || loading}
            >
              Previous
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Showing {skip + 1} - {skip + logs.length}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={logs.length < limit || loading}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EmailLogsTab;
